<template>
  <div
    v-loading="isCreatingDetails"
    class="style-detail-controller"
  >
    <div
      :class="[
        'style-detail-controller__header',
        {
          'style-detail-controller__header--no-children': details.length === 0
        }
      ]"
    >
      <DropArea
        :title="controllerTitle"
        @handle-files="handleFiles"
      />
    </div>

    <Draggable
      v-if="details.length > 0"
      :disabled="!isDraggable"
      class="style-detail-controller__detail-previewer"
      :value="details"
      ghost-class="detail-dragged__placeholder"
      v-bind="{ animation: 150 }"
      @end="onDrop"
    >
      <div
        v-for="detail in details"
        :key="detail.id"
        class="style-detail-controller__wrapper"
      >
        <div class="style-detail-controller__image-group">
          <ImagePreview
            class="style-detail-controller__image-group__image"
            display-only
            :image-url="getTrimmedImage(detail.assets_file)"
          />
          <div
            class="style-detail-controller__detail-delete"
            @click.once="removeDetail(detail.id)"
          >
            <i class="el-icon-close" />
          </div>
        </div>
      </div>
    </Draggable>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import Draggable from 'vuedraggable'

import { imageResolutions } from '@/constants/image.js'

import DropArea from '@/components/DropArea.vue'
import ImagePreview from '@/components/ImagePreview.vue'

import { DragAndDrop } from '@/mixins/DragAndDrop'

import { S3Trim } from '@/mixins/s3Trim.js'

export default {
  name: 'StyleDetailController',

  components: {
    DropArea,
    ImagePreview,
    Draggable
  },

  mixins: [DragAndDrop, S3Trim],

  props: {
    controllerTitle: String,
    styleId: {
      required: true,
      type: Number
    }
  },

  data: () => ({
    isCreatingDetails: false,
    isDraggable: true
  }),

  computed: {
    ...mapGetters(['getDetailsByStyleId']),

    /**
     * @returns {string}
     */
    imageResolution () {
      return imageResolutions.DETAIL_THUMBNAIL
    },

    /**
     * @returns {object}
     */
    details () {
      return this.getDetailsByStyleId(this.styleId)
    }
  },

  methods: {
    ...mapActions(['createDetail', 'deleteDetail']),

    /**
     * @param   {number} index
     *
     * @returns {object}
     */
    getDetailByListIndex (index) {
      return this.details[index]
    },

    /**
     * @param {CustomEvent} dragEvent
     */
    onDrop (dragEvent) {
      this.isDraggable = false
      this.stopDraggingReorder({
        type: 'detail',
        destinationIndex: dragEvent.newDraggableIndex,
        item: this.getDetailByListIndex(dragEvent.oldDraggableIndex),
        list: this.details
      })
      this.isDraggable = true
    },

    /**
     * @param {File[]} files
     */
    async handleFiles (files) {
      this.isDraggable = false
      this.isCreatingDetails = true

      for (const file of files) {
        await this.createNewDetail(file)
      }

      this.isCreatingDetails = false
      this.isDraggable = true
    },

    /**
     * @param   {File}   file
     *
     * @returns {object}
     */
    async createNewDetail (file) {
      this.isDraggable = false
      let latestIndex = 0

      if (this.details.length > 0) {
        latestIndex = this.details[this.details.length - 1].index
      }

      const detail = await this.createDetail({
        styleId: this.styleId,
        index: latestIndex + 1,
        file: file
      })
      this.isDraggable = true

      return detail
    },

    /**
     * @param   {string} url
     *
     * @returns {string}
     */
    getTrimmedImage (url) {
      return this.s3Trim(url, this.imageResolution)
    },

    /**
     * @param {number} detailId
     */
    async removeDetail (detailId) {
      this.isDraggable = false
      const payload = {
        styleId: this.styleId,
        detailId
      }

      await this.deleteDetail(payload)
      this.isDraggable = true
    }
  }
}
</script>

<style lang="scss" scoped>
$image-size: spacing(12);

.style-detail-controller__header {
  border-bottom: $border-grey;

  &.style-detail-controller__header--no-children {
    border-bottom: 0;
  }
}

.style-detail-controller {
  display: flex;
  flex-direction: column;
  max-height: 100%;
  background-color: $white;
  border: $border-grey-extra-light;
  border-radius: $border-radius-default;
}

.style-detail-controller__wrapper {
  @include size(100%);

  border-radius: $border-radius-default;
}

.style-detail-controller__image-group {
  position: relative;
  width: 100%;
  height: 100%;
  background-color: $border-grey-extra-light;

  .style-detail-controller__detail-delete {
    position: absolute;
    top: spacing(0.5);
    right: spacing(0.5);
    color: $red;
    font-weight: $font-weight-bold;
    font-size: $font-size-large;
    cursor: pointer;
    opacity: 0;
  }

  &:hover {
    .style-detail-controller__detail-delete {
      opacity: 1;
    }
  }
}

.style-detail-controller__detail-previewer {
  @include scroll-y-smooth;

  display: grid;
  grid-auto-rows: $image-size;
  grid-gap: $spacer-unit-x2;
  grid-template-columns: repeat(auto-fill, $image-size);
  padding: $spacer-unit-x2;
}

.detail-dragged__placeholder {
  @include element-dragged-placeholder;
}
</style>
